import React from "react";

const styles = {
  main: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "48px 20px",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    lineHeight: 1.6,
    overflow: "scroll",
    height: "100%",
    color: "white",
    textRendering: "geometricPrecision"
  },
  h1: { fontSize: "2rem", margin: "0 0 0.5rem" },
  meta: { margin: "0 0 2rem", opacity: 0.8 },
  h2: { marginTop: "2rem", fontSize: "1.25rem" },
  p: { fontSize: "1rem", margin: "0.75rem 0" },
  ul: { margin: "0.5rem 0 0.75rem 1.25rem" },
  ol: { margin: "0.5rem 0 0.75rem 1.25rem" },
  li: { margin: "0.4rem 0" },
  link: { textUnderlineOffset: 3 },
  footer: { marginTop: "3rem", opacity: 0.75, fontSize: "0.95rem" },
};

export function PrivacyPolicyMetaToXSync() {
  return (
    <main style={styles.main}>
      <header>
        <h1 style={styles.h1}>Privacy Policy — Meta To X Sync</h1>
        <p style={styles.meta}>
          <strong>Effective date:</strong> January 16, 2026
        </p>
      </header>

      <section>
        <h2 style={styles.h2}>1) Overview</h2>
        <p style={styles.p}>
          Meta To X Sync is a personal automation tool operated by its owner to
          repost publicly available content about public figures to the
          operator’s Twitter/X account. The tool is not offered as a public
          service and does not provide user accounts, registration, or user
          authentication.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>2) Information we collect</h2>
        <p style={styles.p}>
          Meta To X Sync does not intentionally collect personal information
          from visitors or users. The tool has no end users.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>3) Public content processed</h2>
        <p style={styles.p}>
          The tool may access and process publicly available content (for
          example, publicly accessible posts/stories by public figures) solely
          for the purpose of creating reposts to the operator’s Twitter/X feed.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>4) API credentials</h2>
        <p style={styles.p}>
          The tool may store and use Twitter/X API credentials (such as API
          keys/tokens) required to publish posts. These credentials are used
          only by the operator and are not sold or shared.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>5) Sharing</h2>
        <p style={styles.p}>
          Content is shared to Twitter/X only when posted. Meta To X Sync does
          not sell personal data.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>6) Retention</h2>
        <p style={styles.p}>
          The tool may generate minimal operational logs (if enabled) for
          debugging and reliability. Any such logs are retained only as long as
          necessary, or not stored at all depending on configuration.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>7) Contact</h2>
        <p style={styles.p}>
          Questions or requests:{" "}
          <a href="mailto:contact@davidsaginashvili.com" style={styles.link}>
            contact@davidsaginashvili.com
          </a>
        </p>
      </section>

      <footer style={styles.footer}>
        <p style={styles.p}>
          This policy may be updated from time to time. If it changes, the
          “Effective date” above will be updated.
        </p>
      </footer>
    </main>
  );
}

export function DataDeletionMetaToXSync() {
  return (
    <main style={styles.main}>
      <header>
        <h1 style={styles.h1}>Data Deletion Instructions — Meta To X Sync</h1>
        <p style={styles.meta}>
          <strong>Last updated:</strong> January 16, 2026
        </p>
      </header>

      <section>
        <h2 style={styles.h2}>Who this applies to</h2>
        <p style={styles.p}>
          Meta To X Sync is a personal automation tool and does not provide user
          accounts, registration, or user authentication. If you are not the
          operator of this tool, we do not store personal data about you.
        </p>
      </section>

      <section>
        <h2 style={styles.h2}>What data may exist</h2>
        <p style={styles.p}>
          Depending on configuration, the tool may store:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Twitter/X API credentials</strong> (API keys/tokens) used by
            the operator to publish posts
          </li>
          <li style={styles.li}>
            <strong>Operational logs</strong> (e.g., timestamps, error messages)
            used for debugging and reliability
          </li>
          <li style={styles.li}>
            <strong>Cached metadata</strong> related to posting (e.g., source
            links/captions) if caching is enabled
          </li>
        </ul>
      </section>

      <section>
        <h2 style={styles.h2}>How to delete data</h2>
        <ol style={styles.ol}>
          <li style={styles.li}>
            <strong>Revoke Twitter/X access</strong> (if connected):
            <ul style={styles.ul}>
              <li style={styles.li}>
                Twitter/X <em>Settings and privacy</em> →{" "}
                <em>Security and account access</em> → <em>Apps and sessions</em>{" "}
                → <em>Connected apps</em>
              </li>
              <li style={styles.li}>
                Find <strong>Meta To X Sync</strong> and select{" "}
                <strong>Revoke access</strong>
              </li>
            </ul>
          </li>

          <li style={styles.li}>
            <strong>Delete stored credentials and local data</strong> where the
            tool runs:
            <ul style={styles.ul}>
              <li style={styles.li}>
                If secrets are stored in an <code>.env</code> file, delete the
                file (or remove the relevant keys).
              </li>
              <li style={styles.li}>
                If data is stored in a database, delete the relevant tables/rows
                (e.g., cached items/logs).
              </li>
              <li style={styles.li}>
                If the tool runs on a server, delete any application log files
                (or data stored by your logging provider).
              </li>
            </ul>
          </li>

          <li style={styles.li}>
            <strong>Confirm deletion</strong>
            <ul style={styles.ul}>
              <li style={styles.li}>
                After revoking access and deleting local data, the tool can no
                longer post to Twitter/X.
              </li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h2 style={styles.h2}>Request deletion by email</h2>
        <p style={styles.p}>
          If you believe Meta To X Sync has stored data about you and you want
          it deleted, email{" "}
          <a href="mailto:contact@davidsaginashvili.com" style={styles.link}>
            contact@davidsaginashvili.com
          </a>{" "}
          with the subject line{" "}
          <strong>“Meta To X Sync — Data Deletion”</strong> and include any
          relevant details.
        </p>
      </section>

      <footer style={styles.footer}>
        <p style={styles.p}>
          Note: Because Meta To X Sync does not offer user accounts, most people
          will not have any data stored by this tool. The primary stored items
          (if any) are operator-controlled credentials and operational logs.
        </p>
      </footer>
    </main>
  );
}
